// src/pages/shared/NewAppointment.jsx — Redesenhado conforme Figma
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { useApp } from 'contexts/AppContext';
import { appointmentsService } from 'infrastructure/firebase';
import { sendAppointmentConfirmation } from 'infrastructure/emailService';
import { Button, Avatar, FilterChip } from 'shared/components/ui';
import { useMobile } from 'shared/hooks/useMobile';
import { getAvailableSlots } from 'shared/utils/scheduleLogic';

const T = {
  brand: '#6B5876',
  brandDeep: '#6B5876',
  accent: '#C9929A',
  accentBg: '#F5E6E8',
  accentFg: '#7A3C44',
  border: '#E8E5EC',
  s2: '#FAF7FB',
  s3: '#F0EDF3',
  surface: '#fff',
  text: '#3D3447',
  text2: '#6B6070',
  muted: '#A8A1B0',
  success: '#8FC2A6',
  successBg: '#E7F4ED',
  successFg: '#2D5F43',
  sans: "'DM Sans',sans-serif",
  serif: "'Instrument Serif',serif",
};

const DAYS_SHORT = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];
// ── Step Label ────────────────────────────────────────────────
function StepLabel({ n, title, complete, locked }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
      <div style={{
        width:22, height:22, borderRadius:'50%',
        background: complete ? T.success : locked ? T.s3 : T.brand,
        color: complete ? T.successFg : locked ? T.muted : '#fff',
        display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:11, fontWeight:600, fontFamily:T.sans, flexShrink:0,
      }}>
        {complete ? '✓' : n}
      </div>
      <div style={{ fontFamily:T.serif, fontSize:18, color: locked ? T.muted : T.text }}>{title}</div>
    </div>
  );
}

// ── Procedure Grid ────────────────────────────────────────────
function ProcedureGrid({ procedures, value, onChange }) {
  const isMob = window.innerWidth <= 380;
  return (
    <div style={{ display:'grid', gridTemplateColumns: isMob ? '1fr' : '1fr 1fr', gap:10, alignItems:'stretch' }}>
      {procedures.map(p => {
        const sel = value?.id === p.id;
        return (
          <div key={p.id} onClick={() => onChange(p)}
            style={{
              background: sel ? T.accentBg : '#fff',
              border:`1.5px solid ${sel ? T.accent : T.border}`,
              borderRadius:14, padding:'12px 14px',
              display:'flex', alignItems:'center', gap:12, cursor:'pointer',
              transition:'all .15s',
            }}>
            <div style={{ width:44, height:44, borderRadius:12, flexShrink:0,
              background:'linear-gradient(135deg,#B5A2D2,#C9929A)',
              display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>
              {p.emoji || '💆'}
            </div>
            <div style={{ flex:1, minWidth:0, fontFamily:T.sans }}>
              <div style={{
                fontWeight:600, fontSize:13, color:T.text, lineHeight:1.3,
                overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical',
              }}>
                {p.name}
              </div>
              <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>{p.duration}min · R$ {p.price}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Client Picker ────────────────────────────────────────────
function ClientPicker({ clients, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(!value);
  const results = clients.filter(c =>
    !query || c.name?.toLowerCase().includes(query.toLowerCase()) || c.phone?.includes(query)
  );

  if (value && !open) {
    return (
      <div style={{
        background:'#fff', border:`1.5px solid ${T.border}`, borderRadius:14,
        padding:'10px 14px', display:'flex', alignItems:'center', gap:12,
      }}>
        <Avatar name={value.name} color="rose" size="md" />
        <div style={{ flex:1, fontFamily:T.sans }}>
          <div style={{ fontWeight:600, fontSize:13, color:T.text }}>{value.name}</div>
          <div style={{ fontSize:11, color:T.muted, marginTop:1 }}>{value.phone}</div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => { setOpen(true); setQuery(''); }}>Trocar</Button>
      </div>
    );
  }

  return (
    <div style={{ background:'#fff', border:`1.5px solid ${T.border}`, borderRadius:14, overflow:'hidden' }}>
      <div style={{ position:'relative', padding:'10px 12px', borderBottom: results.length ? `1px solid ${T.border}` : 'none' }}>
        <span style={{ position:'absolute', left:22, top:'50%', transform:'translateY(-50%)', color:T.muted, fontSize:13 }}>🔍</span>
        <input
          autoFocus
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Buscar cliente por nome ou telefone..."
          style={{
            width:'100%', border:'none', outline:'none', fontFamily:T.sans, fontSize:13,
            color:T.text, background:'transparent', padding:'4px 4px 4px 28px', boxSizing:'border-box',
          }}
        />
      </div>
      <div style={{ maxHeight:200, overflowY:'auto' }}>
        {results.map(c => (
          <div key={c.id} onClick={() => { onChange(c); setOpen(false); }}
            style={{ padding:'10px 14px', display:'flex', alignItems:'center', gap:10,
              cursor:'pointer', borderBottom:`1px solid ${T.border}`, fontFamily:T.sans,
              background: value?.id === c.id ? T.s2 : 'transparent' }}>
            <Avatar name={c.name} color="rose" size="sm" />
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:500, fontSize:13, color:T.text }}>{c.name}</div>
              <div style={{ fontSize:11, color:T.muted }}>{c.phone}</div>
            </div>
          </div>
        ))}
        {results.length === 0 && (
          <div style={{ padding:24, textAlign:'center', color:T.muted, fontSize:12, fontFamily:T.sans }}>
            Nenhum cliente encontrado
          </div>
        )}
      </div>
    </div>
  );
}

// ── Professional Filter ───────────────────────────────────────
function ProfessionalFilter({ professionals, value, onChange }) {
  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
      <FilterChip label="Qualquer profissional" active={value === 'any'} onClick={() => onChange('any')} />
      {professionals.map(p => (
        <div key={p.id} onClick={() => onChange(p.id)}
          style={{
            padding:'6px 12px 6px 6px', borderRadius:20, cursor:'pointer',
            display:'inline-flex', alignItems:'center', gap:8, fontFamily:T.sans, fontSize:12,
            fontWeight:500, userSelect:'none',
            border:`1px solid ${value === p.id ? T.brand : T.border}`,
            background: value === p.id ? T.brand : T.s3,
            color: value === p.id ? '#fff' : T.text2,
          }}>
          <Avatar name={p.name} color={p.avatarColor} size="sm" />
          <span>{p.name}</span>
        </div>
      ))}
    </div>
  );
}

// ── Calendar ──────────────────────────────────────────────────
// workDays: array de ints 0-6 (0=Dom, 6=Sáb). null = todos os dias liberados.
function Calendar({ year, month, availability, selectedDay, onSelectDay, onPrev, onNext, workDays = null }) {
  const monthOffset = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthLabel  = new Date(year, month, 1).toLocaleDateString('pt-BR', { month:'long', year:'numeric' });
  const nowRef      = new Date();
  // Data local — evita bug de fuso (toISOString retorna UTC, pode mostrar dia errado após 21h no Brasil)
  const todayISO    = `${nowRef.getFullYear()}-${String(nowRef.getMonth()+1).padStart(2,'0')}-${String(nowRef.getDate()).padStart(2,'0')}`;
  const isCurrentMonth = year === nowRef.getFullYear() && month === nowRef.getMonth();

  const cells = [];
  for (let i = 0; i < monthOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div style={{ background:'#fff', border:`1px solid ${T.border}`, borderRadius:14, overflow:'hidden' }}>
      <div style={{ padding:'12px 18px', background:T.brandDeep, color:'#fff',
        display:'flex', alignItems:'center', justifyContent:'space-between', fontFamily:T.sans }}>
        <button onClick={onPrev} disabled={isCurrentMonth}
          style={{ background:'rgba(255,255,255,0.12)', border:'none', color:'#fff',
          width:28, height:28, borderRadius:8, cursor: isCurrentMonth ? 'not-allowed' : 'pointer',
          fontSize:16, opacity: isCurrentMonth ? 0.3 : 1 }}>‹</button>
        <span style={{ fontFamily:T.serif, fontSize:18, textTransform:'capitalize' }}>{monthLabel}</span>
        <button onClick={onNext}
          style={{ background:'rgba(255,255,255,0.12)', border:'none', color:'#fff',
          width:28, height:28, borderRadius:8, cursor:'pointer', fontSize:16 }}>›</button>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', background:T.s2 }}>
        {DAYS_SHORT.map(d => (
          <div key={d} style={{
            textAlign:'center', padding:'8px 0', fontFamily:T.sans, fontSize:10,
            fontWeight:600, textTransform:'uppercase', letterSpacing:1, color:T.muted,
          }}>{d}</div>
        ))}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', background:'#fff' }}>
        {cells.map((d, i) => {
          if (d === null) {
            return <div key={`e${i}`} style={{ minHeight:58, background:T.s2, borderRight:`1px solid ${T.border}`, borderBottom:`1px solid ${T.border}` }} />;
          }
          const dateISO   = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
          const dayOfWeek = new Date(dateISO + 'T12:00:00').getDay();
          // Dia de folga: fora dos dias de funcionamento configurados
          const isDayOff  = workDays !== null && !workDays.includes(dayOfWeek);
          const isPast    = dateISO < todayISO;
          const isToday   = dateISO === todayISO;
          const free      = availability[d] || 0;
          const disabled  = isPast || isDayOff || free === 0;
          const isSel     = d === selectedDay;

          return (
            <div key={d} onClick={() => !disabled && onSelectDay(d)}
              title={isDayOff ? 'Dia de folga do estabelecimento' : undefined}
              style={{
                minHeight:58, padding:'6px 8px', cursor: disabled ? 'not-allowed' : 'pointer',
                borderRight:`1px solid ${T.border}`, borderBottom:`1px solid ${T.border}`,
                background: isSel ? T.brand : isToday && !disabled ? T.accentBg : disabled ? T.s2 : '#fff',
                opacity: disabled ? .45 : 1, fontFamily:T.sans,
                transition:'background .15s',
              }}>
              <div style={{
                fontSize:13, fontWeight:500,
                color: isSel ? '#fff' : isToday ? T.accentFg : T.text2,
              }}>{d}</div>
              {isDayOff && !isPast && (
                <div style={{ fontSize:8, color:T.muted, marginTop:2 }}>folga</div>
              )}
              {!disabled && free > 0 && (
                <div style={{
                  fontSize:9, marginTop:4, fontWeight:500,
                  color: isSel ? 'rgba(255,255,255,0.78)' : T.muted,
                }}>
                  {free} {free === 1 ? 'horário' : 'horários'}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Slot Picker ───────────────────────────────────────────────
function SlotPicker({ professionals, selectedDay, calMonth, calYear, selectedSlot, onSelect, slotsByPro = {} }) {
  const isMob = useMobile();
  if (!selectedDay) return null;
  if (professionals.length === 0) {
    return (
      <div style={{ background:'#fff', border:`1px dashed ${T.border}`, borderRadius:14, padding:'32px 24px', textAlign:'center', fontFamily:T.sans }}>
        <div style={{ fontSize:32, opacity:.4, marginBottom:8 }}>👤</div>
        <div style={{ fontFamily:T.serif, fontSize:16, color:T.text, marginBottom:4 }}>Nenhum profissional disponível</div>
        <p style={{ fontSize:13, color:T.muted }}>Este procedimento não tem profissionais vinculados. O administrador precisa vincular ao menos um profissional.</p>
      </div>
    );
  }
  const monthNum = (calMonth ?? new Date().getMonth()) + 1;
  const yr       = calYear ?? new Date().getFullYear();

  return (
    <div style={{ background:'#fff', border:`1px solid ${T.border}`, borderRadius:14, padding:'16px 18px' }}>
      <div style={{ fontFamily:T.sans, fontSize:11, fontWeight:600, color:T.muted, textTransform:'uppercase', letterSpacing:1, marginBottom:14 }}>
        Horários disponíveis · {selectedDay}/{String(monthNum).padStart(2,'0')}/{yr}
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:18 }}>
        {professionals.map(pro => {
          const freeSlots = slotsByPro[pro.id] || [];
          return (
            <div key={pro.id}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                <Avatar name={pro.name} color={pro.avatarColor} size="sm" />
                <div style={{ fontFamily:T.sans }}>
                  <div style={{ fontWeight:600, fontSize:13, color:T.text }}>{pro.name}</div>
                  <div style={{ fontSize:11, color:T.muted }}>
                    {pro.specialty} · {freeSlots.length} horário{freeSlots.length !== 1 ? 's' : ''} livre{freeSlots.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns: isMob ? 'repeat(4,1fr)' : 'repeat(6,1fr)', gap:6 }}>
                {freeSlots.map(time => {
                  const isSel = selectedSlot?.proId === pro.id && selectedSlot?.time === time;
                  return (
                    <div key={time}
                      onClick={() => onSelect({ proId: pro.id, time })}
                      style={{
                        padding:'9px 4px', textAlign:'center', borderRadius:10,
                        fontSize:12, fontWeight:500, fontFamily:T.sans, cursor:'pointer',
                        border:`1.5px solid ${isSel ? T.brand : T.border}`,
                        background: isSel ? T.brand : T.s2,
                        color: isSel ? '#fff' : T.text2,
                        transition:'all .15s',
                      }}>
                      {time}
                    </div>
                  );
                })}
                {freeSlots.length === 0 && (
                  <div style={{ gridColumn:'1 / -1', textAlign:'center', padding:'10px 0', fontSize:12, color:T.muted, fontFamily:T.sans, fontStyle:'italic' }}>
                    Sem horários disponíveis — dia bloqueado ou sem encaixe para este procedimento.
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Empty State ───────────────────────────────────────────────
function EmptyState({ ico, title, text }) {
  return (
    <div style={{
      background:'#fff', border:`1px dashed ${T.border}`, borderRadius:14,
      padding:'48px 24px', textAlign:'center', fontFamily:T.sans,
    }}>
      <div style={{ fontSize:40, opacity:.4, marginBottom:8 }}>{ico}</div>
      <div style={{ fontFamily:T.serif, fontSize:18, color:T.text, marginBottom:4 }}>{title}</div>
      <p style={{ fontSize:13, color:T.muted, lineHeight:1.5, maxWidth:360, margin:'0 auto' }}>{text}</p>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────
export default function NewAppointment() {
  const { userProfile, isAdmin, isProfessional, isClient, estabelecimentoId } = useAuth();
  const { professionals, clients, approvedProcedures, showToast, appointments, scheduleWindows, businessConfig } = useApp();
  const navigate  = useNavigate();
  const location  = useLocation();

  // Contexto passado pelo Hub (CatalogPage ou ClinicProfilePage via router state)
  // Para clientes: obrigatório ter establishmentId ou professionalId para iniciar o agendamento
  const bookingCtx = (location.state || {});
  const ctxEstabId   = bookingCtx.establishmentId   || null;
  const ctxEstabName = bookingCtx.establishmentName  || null;
  const ctxProId     = bookingCtx.professionalId     || null;
  const ctxProName   = bookingCtx.professionalName   || null;
  const ctxProcId    = bookingCtx.procedureId        || null;

  // Clientes sem contexto de Hub → redireciona ao catálogo para escolher um estabelecimento primeiro
  useEffect(() => {
    if (isClient && !ctxEstabId && !ctxProId) {
      navigate('/client/catalog', { replace: true });
    }
  }, [isClient, ctxEstabId, ctxProId]); // eslint-disable-line

  const nowRef = new Date();
  // Data local do dispositivo (evita bug de fuso: toISOString usa UTC)
  const todayLocalISO = `${nowRef.getFullYear()}-${String(nowRef.getMonth()+1).padStart(2,'0')}-${String(nowRef.getDate()).padStart(2,'0')}`;
  const [procedure, setProcedure] = useState(null);
  const [client, setClient] = useState(isClient ? {
    id:    userProfile?.id,
    name:  userProfile?.name,
    email: userProfile?.email || '',
    phone: userProfile?.phone || '',
  } : null);
  // Se o contexto tem profissional específico (autônomo), pré-seleciona; senão 'any'
  const [proFilter, setProFilter]     = useState(ctxProId || 'any');
  const [selectedDay, setDay]         = useState(null);
  const [selectedSlot, setSlot]       = useState(null);
  const [loading, setLoading]         = useState(false);
  const [calYear, setCalYear]         = useState(nowRef.getFullYear());
  const [calMonth, setCalMonth]       = useState(nowRef.getMonth()); // 0-indexed

  const goMonthPrev = () => {
    if (calYear === nowRef.getFullYear() && calMonth === nowRef.getMonth()) return; // não vai atrás do mês atual
    setDay(null); setSlot(null);
    if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11); }
    else setCalMonth(m => m - 1);
  };
  const goMonthNext = () => {
    setDay(null); setSlot(null);
    if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0); }
    else setCalMonth(m => m + 1);
  };

  // When procedure changes, reset downstream
  // Preserva ctxProId se cliente está agendando com profissional autônomo específico
  const handleProcedure = (p) => {
    setProcedure(p);
    setProFilter(ctxProId || 'any');
    setDay(null);
    setSlot(null);
  };
  const handleProFilter = (id) => {
    setProFilter(id);
    setDay(null);
    setSlot(null);
  };
  const handleDay = (d) => { setDay(d); setSlot(null); };

  // Profissionais do contexto: para clientes com estab. ou profissional pré-definido,
  // limita a lista àqueles que pertencem ao contexto
  const contextualProfessionals = useMemo(() => {
    if (ctxEstabId) return professionals.filter(p => p.estabelecimentoId === ctxEstabId);
    if (ctxProId)   return professionals.filter(p => p.id === ctxProId);
    return professionals;
  }, [professionals, ctxEstabId, ctxProId]);

  // Get professionals who can do this procedure (limitado ao contexto quando cliente)
  const allowedPros = useMemo(() => {
    if (!procedure) return [];
    const basePros = (isClient && (ctxEstabId || ctxProId)) ? contextualProfessionals : professionals;
    // Checa professionals[] (padrão) e professionalId (legado) — [] vazio NÃO conta como preenchido
    const profIds = (procedure.professionals?.length > 0)
      ? procedure.professionals
      : (procedure.professionalId ? [procedure.professionalId] : []);
    const pros = profIds.length > 0
      ? basePros.filter(p => profIds.includes(p.id))
      : basePros; // sem vínculo: mostra todos do contexto
    if (proFilter === 'any') return pros;
    return pros.filter(p => p.id === proFilter);
  }, [procedure, proFilter, professionals, contextualProfessionals, isClient, ctxEstabId, ctxProId]);

  // Disponibilidade mensal real — usa calYear/calMonth para suportar navegação
  const availability = useMemo(() => {
    if (!procedure || allowedPros.length === 0) return {};
    const out = {};
    const days   = new Date(calYear, calMonth + 1, 0).getDate();
    const todayStr  = todayLocalISO;
    const workDays  = businessConfig?.workDays ?? null;
    const workStart = businessConfig?.workStart ?? '08:00';
    const workEnd   = businessConfig?.workEnd   ?? '18:00';

    // PERFORMANCE: pré-filtra agendamentos/janelas para o mês + profissionais relevantes
    // UMA vez, em vez de varrer os arrays completos a cada (dia × profissional).
    const monthPrefix = `${calYear}-${String(calMonth + 1).padStart(2,'0')}-`;
    const proIdSet = new Set(allowedPros.map(p => p.id));
    const monthAppts = appointments.filter(a =>
      proIdSet.has(a.professionalId) && (a.date || '').startsWith(monthPrefix));
    const monthWins = scheduleWindows.filter(w =>
      (w.allProfessionals || proIdSet.has(w.professionalId)) && (w.date || '').startsWith(monthPrefix));

    for (let d = 1; d <= days; d++) {
      const date = `${calYear}-${String(calMonth + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      if (date < todayStr) { out[d] = 0; continue; }
      const dow = new Date(date + 'T12:00:00').getDay();
      if (workDays !== null && !workDays.includes(dow)) { out[d] = 0; continue; }
      let total = 0;
      for (const pro of allowedPros) {
        total += getAvailableSlots({
          date,
          professionalId: pro.id,
          procedureDuration: procedure.duration,
          appointments: monthAppts,
          windows: monthWins,
          workStart,
          workEnd,
        }).length;
      }
      out[d] = total;
    }
    return out;
  }, [procedure, allowedPros, appointments, scheduleWindows, calYear, calMonth, businessConfig]); // eslint-disable-line

  // Slots livres por profissional no dia selecionado
  const slotsByPro = useMemo(() => {
    if (!selectedDay || allowedPros.length === 0 || !procedure) return {};
    const date      = `${calYear}-${String(calMonth + 1).padStart(2,'0')}-${String(selectedDay).padStart(2,'0')}`;
    const workStart = businessConfig?.workStart ?? '08:00';
    const workEnd   = businessConfig?.workEnd   ?? '18:00';
    const out = {};
    for (const pro of allowedPros) {
      out[pro.id] = getAvailableSlots({
        date,
        professionalId:    pro.id,
        procedureDuration: procedure.duration,
        appointments,
        windows:           scheduleWindows,
        workStart,
        workEnd,
      });
    }
    return out;
  }, [selectedDay, allowedPros, procedure, appointments, scheduleWindows, calYear, calMonth, businessConfig]); // eslint-disable-line

  const isMobile = useMobile();
  const canConfirm = procedure && client && selectedDay && selectedSlot;
  const selectedPro = selectedSlot ? professionals.find(p => p.id === selectedSlot.proId) : null;

  const handleSubmit = async () => {
    if (!canConfirm) return;
    setLoading(true);
    try {
      const dateStr = `${calYear}-${String(calMonth + 1).padStart(2,'0')}-${String(selectedDay).padStart(2,'0')}`;
      
      // Cria o agendamento com status 'pending'; a Cloud Function enviará
      // notificação e e-mail de confirmação de presença 1 dia e 5h antes.
      // Inferir estabelecimentoId em ordem de prioridade:
      // 1. Usuário logado (admin/profissional da clínica)
      // 2. Contexto do Hub (cliente veio de clínica específica)
      // 3. Profissional selecionado
      const effectiveEstabId = estabelecimentoId
        || ctxEstabId
        || selectedPro?.estabelecimentoId
        || null;

      await appointmentsService.add({
        clientId:          client.id,
        clientName:        client.name,
        clientEmail:       client.email || '',
        professionalId:    selectedSlot.proId,
        professionalName:  selectedPro?.name || '',
        procedureId:       procedure.id,
        procedureName:     procedure.name,
        date:              dateStr,
        time:              selectedSlot.time,
        price:             procedure.price || 0,
        duration:          procedure.duration || 60,
        createdBy:         userProfile?.id,
        estabelecimentoId: effectiveEstabId,
      });

      // Envia e-mail de confirmação de reserva (não bloqueia o fluxo se falhar)
      if (client.email) {
        sendAppointmentConfirmation({
          clientName:       client.name,
          clientEmail:      client.email,
          professionalName: selectedPro?.name || '',
          procedureName:    procedure.name,
          date:             dateStr,
          time:             selectedSlot.time,
          price:            procedure.price,
        }).catch(err => console.warn('[Email] Falha ao enviar confirmação:', err));
      }

      showToast(`Agendado! ${client.name} · ${procedure.name} · ${selectedDay}/${calMonth+1}/${calYear} às ${selectedSlot.time}`);
      
      const back = isAdmin ? '/admin/appointments' : isProfessional ? '/pro/schedule' : '/client/agenda';
      navigate(back);
    } catch (err) {
      console.error('[NewAppointment] Erro ao criar agendamento:', err?.code || err?.message || err);
      showToast('Erro ao agendar. Tente novamente.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // ── Filtragem de procedimentos por contexto de agendamento ──────────────────
  // Regra: procedimento deve sempre pertencer ao estabelecimento/profissional selecionado.
  const proceduresForBooking = useMemo(() => {
    // Profissional logado: vê apenas seus procedimentos
    if (isProfessional) {
      return approvedProcedures.filter(p => {
        const profIds = (p.professionals?.length > 0)
          ? p.professionals
          : (p.professionalId ? [p.professionalId] : []);
        if (profIds.length === 0) return true;
        return profIds.includes(userProfile?.id);
      });
    }

    // Admin: vê todos os procedimentos do seu contexto (sem restrição de Hub)
    if (isAdmin) return approvedProcedures;

    // Cliente com contexto de estabelecimento: filtra pelo estabelecimento
    if (isClient && ctxEstabId) {
      return approvedProcedures.filter(p => {
        // Novo modelo: usa estabelecimentoId
        if (p.estabelecimentoId) return p.estabelecimentoId === ctxEstabId;
        // Fallback legacy: verifica profissionais vinculados ao estabelecimento
        const profIds = (p.professionals?.length > 0) ? p.professionals : (p.professionalId ? [p.professionalId] : []);
        return profIds.some(pid => professionals.some(pr => pr.id === pid && pr.estabelecimentoId === ctxEstabId));
      });
    }

    // Cliente com contexto de profissional autônomo: filtra pelos procedimentos do profissional
    if (isClient && ctxProId) {
      return approvedProcedures.filter(p => {
        const profIds = (p.professionals?.length > 0) ? p.professionals : (p.professionalId ? [p.professionalId] : []);
        return profIds.includes(ctxProId);
      });
    }

    return approvedProcedures;
  }, [approvedProcedures, isProfessional, isAdmin, isClient, ctxEstabId, ctxProId, userProfile, professionals]);

  // Compatibilidade: proceduresForPro é alias de proceduresForBooking
  const proceduresForPro = proceduresForBooking;

  // Auto-seleciona o procedimento quando vem com procedureId no contexto da rota.
  // POSIÇÃO OBRIGATÓRIA: deve estar APÓS a declaração de proceduresForBooking
  // para evitar ReferenceError por temporal dead zone do const.
  useEffect(() => {
    if (!ctxProcId || procedure || proceduresForBooking.length === 0) return;
    const preselect = proceduresForBooking.find(p => p.id === ctxProcId);
    if (preselect) {
      setProcedure(preselect);
      setProFilter(ctxProId || 'any');
      setDay(null);
      setSlot(null);
    }
  }, [ctxProcId, ctxProId, proceduresForBooking]); // eslint-disable-line

  const proListForFilter = useMemo(() => {
    if (!procedure) return [];
    const profIds = (procedure.professionals?.length > 0)
      ? procedure.professionals
      : (procedure.professionalId ? [procedure.professionalId] : []);
    // Para clientes com contexto, usa contextualProfessionals; caso contrário, todos
    const base = (isClient && (ctxEstabId || ctxProId)) ? contextualProfessionals : professionals;
    if (profIds.length === 0) return base;
    return base.filter(p => profIds.includes(p.id));
  }, [procedure, professionals, contextualProfessionals, isClient, ctxEstabId, ctxProId]);

  // Nome do contexto para exibir no header
  const contextLabel = ctxEstabName || ctxProName || null;
  const backToHub = isClient && (ctxEstabId || ctxProId);

  return (
    <div style={{ fontFamily:T.sans, paddingBottom: isMobile ? 160 : 96 }}>
      {/* Banner de contexto — exibido quando cliente vem do Hub com estabelecimento/profissional pré-selecionado */}
      {backToHub && contextLabel && (
        <div style={{ background: T.s3, border: `1px solid ${T.border}`, borderRadius: 12,
          padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 12, fontFamily: T.sans }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>{ctxEstabId ? '🏥' : '👤'}</span>
            <div>
              <div style={{ fontSize: 11, color: T.muted, textTransform: 'uppercase',
                letterSpacing: 1, fontWeight: 600, marginBottom: 1 }}>
                {ctxEstabId ? 'Estabelecimento selecionado' : 'Profissional selecionado'}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{contextLabel}</div>
            </div>
          </div>
          <button onClick={() => navigate(ctxEstabId ? `/client/clinic/${ctxEstabId}` : '/client/catalog')}
            style={{ background: 'none', border: `1px solid ${T.border}`, borderRadius: 8,
              padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: T.brand,
              fontFamily: T.sans, whiteSpace: 'nowrap' }}>
            ← Voltar
          </button>
        </div>
      )}

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <div style={{ fontFamily:T.serif, fontSize:32, color:T.text }}>Novo Agendamento</div>
          <div style={{ fontSize:13, color:T.muted, marginTop:2 }}>
            {backToHub && contextLabel
              ? `Procedimentos disponíveis em ${contextLabel}.`
              : 'Escolha o procedimento, o cliente e a melhor data — só mostraremos horários realmente disponíveis.'}
          </div>
        </div>
        {!backToHub && (
          <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>← Cancelar</Button>
        )}
      </div>

      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(360px, 420px) 1fr', gap:20, alignItems:'start' }}>
        {/* LEFT — setup */}
        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
          <section>
            <StepLabel n={1} title="Procedimento" complete={!!procedure} />
            <ProcedureGrid procedures={proceduresForPro} value={procedure} onChange={handleProcedure} />
          </section>

          <section>
            <StepLabel n={2} title="Cliente" complete={!!client} locked={!procedure} />
            {procedure ? (
              isClient ? (
                <div style={{ padding:'14px 16px', background:T.s2, borderRadius:14, fontSize:13, color:T.text, fontFamily:T.sans }}>
                  Agendando para: <strong>{client.name}</strong>
                </div>
              ) : (
                <ClientPicker clients={clients} value={client} onChange={setClient} />
              )
            ) : (
              <div style={{ padding:'14px 16px', background:T.s2, border:`1px dashed ${T.border}`,
                borderRadius:14, fontSize:12, color:T.muted, fontFamily:T.sans }}>
                Escolha um procedimento primeiro
              </div>
            )}
          </section>

          <section>
            <StepLabel n={3} title="Profissional (opcional)" complete={proFilter !== 'any'} locked={!procedure} />
            {procedure ? (
              <>
                <p style={{ fontSize:12, color:T.muted, marginBottom:10, lineHeight:1.5 }}>
                  Deixe em <b>"Qualquer profissional"</b> para ver todos os horários livres,
                  ou escolha um para filtrar.
                </p>
                <ProfessionalFilter
                  professionals={proListForFilter}
                  value={proFilter}
                  onChange={handleProFilter}
                />
              </>
            ) : (
              <div style={{ padding:'14px 16px', background:T.s2, border:`1px dashed ${T.border}`,
                borderRadius:14, fontSize:12, color:T.muted, fontFamily:T.sans }}>
                Escolha um procedimento primeiro
              </div>
            )}
          </section>
        </div>

        {/* RIGHT — calendar + slots */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {!procedure ? (
            <EmptyState
              ico="💆"
              title="Escolha um procedimento"
              text="Quando você escolher o procedimento, vamos mostrar aqui os dias e horários disponíveis dos profissionais que o realizam."
            />
          ) : (
            <>
              <Calendar
                year={calYear}
                month={calMonth}
                availability={availability}
                selectedDay={selectedDay}
                onSelectDay={handleDay}
                onPrev={goMonthPrev}
                onNext={goMonthNext}
                workDays={businessConfig?.workDays ?? null}
              />
              {selectedDay ? (
                <SlotPicker
                  professionals={allowedPros}
                  selectedDay={selectedDay}
                  calMonth={calMonth}
                  calYear={calYear}
                  selectedSlot={selectedSlot}
                  onSelect={setSlot}
                  slotsByPro={slotsByPro}
                />
              ) : (
                <EmptyState
                  ico="📅"
                  title="Selecione um dia"
                  text="Os dias com pelo menos um horário disponível ficam destacados — com a quantidade de horários livres logo abaixo do número."
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Sticky confirm bar — no mobile, fica acima do bottom nav com safe-area */}
      <div style={{
        position:'fixed', bottom: isMobile ? 'calc(64px + env(safe-area-inset-bottom, 0px))' : 0, left:0, right:0,
        background:'rgba(255,255,255,0.97)', backdropFilter:'blur(8px)',
        borderTop:`1px solid ${T.border}`,
        padding: isMobile ? '10px 14px' : '16px 28px',
        display:'flex', flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? 8 : 16,
        fontFamily:T.sans, zIndex:120,
      }}>
        {!isMobile && (
          <div style={{ flex:1, display:'flex', gap:24, alignItems:'center', flexWrap:'wrap', fontSize:13, color:T.text2 }}>
            {procedure ? (
              <span><span style={{ color:T.muted, fontSize:11, textTransform:'uppercase', letterSpacing:1, marginRight:6 }}>Procedimento</span>{procedure.name}</span>
            ) : <span style={{ color:T.muted }}>Selecione um procedimento</span>}
            {client && <span><span style={{ color:T.muted, fontSize:11, textTransform:'uppercase', letterSpacing:1, marginRight:6 }}>Cliente</span>{client.name}</span>}
            {selectedDay && selectedSlot && (
              <span>
                <span style={{ color:T.muted, fontSize:11, textTransform:'uppercase', letterSpacing:1, marginRight:6 }}>Data</span>
                {String(selectedDay).padStart(2,'0')}/{String(calMonth+1).padStart(2,'0')}/{calYear} às {selectedSlot.time}
                {selectedPro && <span style={{ color:T.muted, marginLeft:6 }}>· com {selectedPro.name}</span>}
              </span>
            )}
            {procedure && (
              <span style={{ marginLeft:'auto', fontFamily:T.serif, fontSize:22, color:T.text }}>R$ {procedure.price}</span>
            )}
          </div>
        )}
        {isMobile && procedure && (
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:T.text2 }}>
            <span style={{ fontWeight:500 }}>{procedure.name}</span>
            <span style={{ fontFamily:T.serif, fontSize:18, color:T.text }}>R$ {procedure.price}</span>
          </div>
        )}
        <Button variant="primary" size="lg" disabled={!canConfirm} loading={loading}
          onClick={handleSubmit}>
          Confirmar Agendamento →
        </Button>
      </div>
    </div>
  );
}
